import React from 'react';
import { TextBoxComponent, NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { DurationInput } from '../../components/DurationInput';
import { type CrudFormProps } from '../../components/CrudPage';
import { type ResourceTypeDto } from '../../api';
import { FormDialog } from '../../components/FormDialog';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '../../utils/zodResolver';
import { resourceTypeSchema } from '../../utils/validationSchemas';
import { resolveValidationMessage } from '../../utils/validationMessages';
import { Controller } from 'react-hook-form';
import type { Control, FormState, Resolver, UseFormSetValue, UseFormTrigger, UseFormWatch } from 'react-hook-form';
import type { TFunction } from 'i18next';
import { isModuleLicensed } from '../../utils/license';
import { useStore } from '../../../store';

type ResourceTypeWithId = ResourceTypeDto & { id: string };

type ResourceTypeFormFieldsProps = {
  value: ResourceTypeWithId | null;
  control: Control<ResourceTypeWithId>;
  formState: FormState<ResourceTypeWithId>;
  watch: UseFormWatch<ResourceTypeWithId>;
  setValue: UseFormSetValue<ResourceTypeWithId>;
  trigger: UseFormTrigger<ResourceTypeWithId>;
  t: TFunction;
};

type DropDownChangeEvent = {
  value: string | null | undefined;
};

type NumericChangeEvent = {
  value: number | string | null | undefined;
};

const ResourceTypeFormFields: React.FC<ResourceTypeFormFieldsProps> = ({
  value,
  control,
  formState,
  watch,
  setValue,
  trigger,
  t
}) => {
  const { errors } = formState;
  const currentCategory = watch('category');
  const userProfile = useStore((state) => state.userProfile);
  const showRoughPlanning = isModuleLicensed(userProfile, 'PLANNING');

  React.useEffect(() => {
    setValue('batchProcessing', false);
    setValue('synchronicity', false);
  }, [value, setValue]);

  React.useEffect(() => {
    if (!value) {
      setValue('category', 'OTHERS', { shouldValidate: false });
    }
  }, [value, setValue]);

  return (
    <>
      {/* Name and Category in a row */}
      <div className="mb-4 grid grid-cols-2 gap-4">
        <div>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextBoxComponent
                {...field}
                placeholder={t('form.name') + ' *'}
                floatLabelType="Never"
                cssClass={errors.name ? 'e-error' : ''}
              />
            )}
          />
          {errors.name && (
            <div className="text-red-500 text-sm mt-1">
              {resolveValidationMessage(t, errors.name, { max: 100 })}
            </div>
          )}
        </div>
        <div>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <DropDownListComponent
                dataSource={[
                  { text: t('resourceType.category.oven'), value: 'OVEN' },
                  { text: t('resourceType.category.processingLine'), value: 'PROCESSING_LINE' },
                  { text: t('resourceType.category.others'), value: 'OTHERS' }
                ]}
                fields={{ text: 'text', value: 'value' }}
                placeholder={t('resourceType.form.category') + ' *'}
                floatLabelType="Never"
                cssClass={errors.category ? 'e-error' : ''}
                value={field.value || 'OTHERS'}
                change={(e: DropDownChangeEvent) => {
                  const newCategory = e.value ?? undefined;
                  field.onChange(newCategory);

                  if (newCategory === 'OVEN' || newCategory === 'PROCESSING_LINE') {
                    setValue('singleResource', true);
                  }

                  if (newCategory === 'OVEN') {
                    if (!watch('ovenType')) {
                      setValue('ovenType', 'STONE_OVEN');
                    }
                  } else {
                    setValue('ovenType', undefined);
                    setValue('numStoves', undefined);
                    setValue('heatingPhaseSecondsPerDegree', undefined);
                    setValue('coolingPhaseSecondsPerDegree', undefined);
                  }
                  setValue('batchProcessing', false);
                  setValue('synchronicity', false);
                  trigger('ovenType');
                  trigger('numStoves');
                  trigger('singleResource');
                }}
              />
            )}
          />
          {errors.category && (
            <div className="text-red-500 text-sm mt-1">
              {resolveValidationMessage(t, errors.category)}
            </div>
          )}
        </div>
      </div>

      {/* ovenType, numStoves and the oven phase fields (only when category = OVEN) */}
      {watch('category') === 'OVEN' && (
        <>
          {/* Dropdown for the oven type and numStoves side by side */}
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <DropDownListComponent
                dataSource={[
                  { text: t('resourceType.ovenType.stoneOven'), value: 'STONE_OVEN' },
                  { text: t('resourceType.ovenType.rackOven'), value: 'RACK_OVEN' },
                  { text: t('resourceType.ovenType.continuousOven'), value: 'CONTINUOUS_OVEN' },
                  { text: t('resourceType.ovenType.fryer'), value: 'FRYER' }
                ]}
                fields={{ text: 'text', value: 'value' }}
                placeholder={t('resourceType.form.ovenType') + ' *'}
                floatLabelType="Never"
                value={watch('ovenType') || 'STONE_OVEN'}
                enabled={true}
                cssClass={errors.ovenType ? 'e-error' : ''}
                change={(e: DropDownChangeEvent) => {
                  const newOvenType = e.value ?? undefined;
                  setValue('ovenType', newOvenType);
                  // When it is not STONE_OVEN, reset numStoves
                  if (newOvenType !== 'STONE_OVEN') {
                    setValue('numStoves', undefined);
                  }
                  trigger('ovenType');
                  trigger('numStoves');
                }}
              />
              {errors.ovenType && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.ovenType)}
                </div>
              )}
            </div>
            <div>
              <Controller
                name="numStoves"
                control={control}
                render={({ field }) => (
                  <NumericTextBoxComponent
                    placeholder={watch('ovenType') === 'STONE_OVEN' ? t('resourceType.form.numStoves') + ' *' : t('resourceType.form.numStoves')}
                    floatLabelType="Never"
                    format="n0"
                    min={1}
                    value={field.value ?? undefined}
                    enabled={watch('ovenType') === 'STONE_OVEN'}
                    change={(e: NumericChangeEvent) => {
                      const numValue = e.value != null
                        ? (typeof e.value === 'number' ? e.value : parseInt(e.value.toString()))
                        : undefined;
                      field.onChange(numValue);
                      trigger('numStoves');
                    }}
                    cssClass={errors.numStoves ? 'e-error' : ''}
                  />
                )}
              />
              {errors.numStoves && (
                <div className="text-red-500 text-sm mt-1">
                  {errors.numStoves.message === 'Expected number, received null' || errors.numStoves.message?.includes('null')
                    ? t('validation.required')
                    : resolveValidationMessage(t, errors.numStoves)}
                </div>
              )}
            </div>
          </div>
          
          {/* Heat-up phase and cool-down phase side by side */}
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('resourceType.form.heatingPhaseSecondsPerDegree')} *
              </label>
              <DurationInput
                value={watch('heatingPhaseSecondsPerDegree') ?? undefined}
                onChange={(value) => {
                  setValue('heatingPhaseSecondsPerDegree', value ?? undefined, { shouldValidate: true, shouldDirty: true });
                  trigger('heatingPhaseSecondsPerDegree');
                }}
                required={true}
              />
            {errors.heatingPhaseSecondsPerDegree && (
              <div className="text-red-500 text-sm mt-1">
                {resolveValidationMessage(t, errors.heatingPhaseSecondsPerDegree)}
              </div>
            )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('resourceType.form.coolingPhaseSecondsPerDegree')} *
              </label>
              <DurationInput
                value={watch('coolingPhaseSecondsPerDegree') ?? undefined}
                onChange={(value) => {
                  setValue('coolingPhaseSecondsPerDegree', value ?? undefined, { shouldValidate: true, shouldDirty: true });
                  trigger('coolingPhaseSecondsPerDegree');
                }}
                required={true}
              />
            {errors.coolingPhaseSecondsPerDegree && (
              <div className="text-red-500 text-sm mt-1">
                {resolveValidationMessage(t, errors.coolingPhaseSecondsPerDegree)}
              </div>
            )}
            </div>
          </div>
        </>
      )}

      <div className="mb-4">
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextBoxComponent
              {...field}
              value={field.value ?? ''}
              placeholder={t('form.description')}
              floatLabelType="Never"
              multiline={true}
              cssClass={errors.description ? 'e-error' : ''}
            />
          )}
        />
        {errors.description && (
          <div className="text-red-500 text-sm mt-1">{errors.description.message}</div>
        )}
      </div>

      {/* Resource Type Flags */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <Controller
            name="singleResource"
            control={control}
            render={({ field }) => (
              <CheckBoxComponent
                label={t('resourceType.form.singleResource')}
                cssClass="mr-2"
                checked={currentCategory === 'OVEN' || currentCategory === 'PROCESSING_LINE' ? true : !!field.value}
                disabled={currentCategory === 'OVEN' || currentCategory === 'PROCESSING_LINE'}
                change={(e: { checked?: boolean }) => {
                  field.onChange(e.checked);
                  setValue('batchProcessing', false);
                  setValue('synchronicity', false);
                }}
              />
            )}
          />
          <span 
            className="text-blue-600 cursor-help text-sm" 
            title={`${t('resourceType.form.singleResourceDescription')}\n${currentCategory === 'OVEN' || currentCategory === 'PROCESSING_LINE' ? t('resourceType.form.singleResourceRequiredForSetupTimes') : t('resourceType.form.singleResourceMandatory')}`}
            style={{ fontSize: '14px' }}
          >
            ⓘ
          </span>
        </div>
      </div>

      {/* Batch Processing and Synchronicity removed - Application no longer supports batch processing */}

      {/* Rough planning: usable share of the shift hours (docs/CONCEPT_Grobplanung.md, decision 115) */}
      {showRoughPlanning && (
        <div className="mb-4 max-w-xs">
          <label
            className="block text-sm font-medium text-gray-700 mb-1"
            title={t('resourceType.form.roughPlanningUsabilityPercentHelp', 'Anteil der Schichtstunden, den die Grobplanung verplanen darf. 100 % = kein Abschlag für Rüsten und Verluste.')}
          >
            {t('resourceType.form.roughPlanningUsabilityPercent', 'Nutzbarkeit Grobplanung (%)')}
          </label>
          <NumericTextBoxComponent
            value={watch('roughPlanningUsabilityPercent') ?? 100}
            change={(e: NumericChangeEvent) => {
              // An empty field means the default 100 %, so the payload matches what the field shows
              const raw = e.value != null && e.value !== '' ? Number(e.value) : 100;
              setValue('roughPlanningUsabilityPercent', raw, { shouldValidate: true, shouldDirty: true });
            }}
            format="n0"
            min={1}
            max={100}
            step={1}
            floatLabelType="Never"
            cssClass={errors.roughPlanningUsabilityPercent ? 'e-error' : ''}
          />
          {errors.roughPlanningUsabilityPercent && (
            <div className="text-red-500 text-sm mt-1">
              {resolveValidationMessage(t, errors.roughPlanningUsabilityPercent)}
            </div>
          )}
        </div>
      )}
    </>
  );
};

const ResourceTypeFormDialog: React.FC<CrudFormProps<ResourceTypeWithId>> = ({
  value,
  onClose,
  onSave
}) => {
  const { t } = useTranslation();

  return (
    <FormDialog
      value={value}
      onClose={onClose}
      onSave={onSave}
      titleKey="dialogs.resourceType"
      width="800px"
      resolver={zodResolver(resourceTypeSchema) as unknown as Resolver<ResourceTypeWithId>}
    >
      {({ control, formState, watch, setValue, trigger }) => (
        <ResourceTypeFormFields
          value={value}
          control={control}
          formState={formState}
          watch={watch}
          setValue={setValue}
          trigger={trigger}
          t={t}
        />
      )}
    </FormDialog>
  );
};

export default ResourceTypeFormDialog;
